import { memo, useContext, useRef } from 'react'
import {
  BaseEdge, EdgeLabelRenderer, getBezierPath, getSmoothStepPath, getStraightPath, Position,
  useReactFlow, useStoreApi, useInternalNode,
  type EdgeProps, type Edge,
} from '@xyflow/react'
import { EditModeContext, DiagramActionsContext } from './diagramNodes'

type XY = { x: number; y: number }
export type PortEdgeData = {
  color?: string; width?: number; dash?: 'solid' | 'dashed' | 'dotted' | 'long'
  arrowStart?: boolean; arrowEnd?: boolean; animated?: boolean
  labelColor?: string; labelSize?: number; labelBg?: string; preset?: string
  sourcePort?: string; targetPort?: string
  // User-dragged offsets (flow units) from each label's automatic position
  sourcePortOffset?: XY; targetPortOffset?: XY; labelOffset?: XY
  // Line shape: `bend` moves the middle of the line; the shifts slide each end along its node side
  bend?: XY; sourceShift?: XY; targetShift?: XY
}
type PortEdgeT = Edge<PortEdgeData>
type PathKind = 'straight' | 'bezier' | 'smoothstep' | 'step'
type OffsetKey = 'sourcePortOffset' | 'targetPortOffset' | 'labelOffset' | 'bend' | 'sourceShift' | 'targetShift'

const PORT_OFFSET = 26

// Port label anchor: a fixed distance from the endpoint. Straight lines go along the line itself;
// curved/stepped paths leave the handle perpendicular to its side, so follow the handle direction.
function portPoint(kind: PathKind, x: number, y: number, pos: Position, ox: number, oy: number) {
  if (kind === 'straight') {
    const dx = ox - x, dy = oy - y, len = Math.hypot(dx, dy) || 1
    const d = Math.min(PORT_OFFSET, len / 3)
    return { x: x + (dx / len) * d, y: y + (dy / len) * d }
  }
  const d = PORT_OFFSET
  if (pos === Position.Top) return { x, y: y - d }
  if (pos === Position.Bottom) return { x, y: y + d }
  if (pos === Position.Left) return { x: x - d, y }
  return { x: x + d, y }
}

const alongX = (pos: Position) => pos === Position.Top || pos === Position.Bottom
const outward = (x: number, y: number, pos: Position, d = 14): XY =>
  pos === Position.Top ? { x, y: y - d } : pos === Position.Bottom ? { x, y: y + d } : pos === Position.Left ? { x: x - d, y } : { x: x + d, y }

// Something on the edge that can be dragged away from its automatic spot in edit mode (labels and
// shape knobs). Clicking selects the edge; double-click puts it back to the automatic position.
// `axis` restricts movement (endpoint knobs slide only along their node side); `limit` clamps it.
function Draggable({ edgeId, at, offset, offsetKey, axis = 'xy', limit, className = '', style, title, children }: {
  edgeId: string; at: XY; offset?: XY; offsetKey: OffsetKey; axis?: 'x' | 'y' | 'xy'; limit?: number
  className?: string; style?: React.CSSProperties; title?: string; children?: React.ReactNode
}) {
  const rf = useReactFlow()
  const store = useStoreApi()
  const editing = useContext(EditModeContext)
  const { beginChange } = useContext(DiagramActionsContext)
  const drag = useRef<{ x: number; y: number; ox: number; oy: number; moved: boolean } | null>(null)
  const o = offset || { x: 0, y: 0 }
  const clamp = (v: number) => limit == null ? v : Math.max(-limit, Math.min(limit, v))
  // Selection-only changes (not setEdges) so clicking doesn't mark the diagram as modified
  const select = () => store.getState().addSelectedEdges([edgeId])
  return (
    <div className={`nodrag nopan ${className}`} title={editing ? (title || 'ลากเพื่อย้ายตำแหน่ง · ดับเบิลคลิกเพื่อคืนตำแหน่งเดิม') : undefined}
      style={{ position: 'absolute', transform: `translate(-50%, -50%) translate(${at.x + o.x}px, ${at.y + o.y}px)`, pointerEvents: 'all', ...style }}
      onPointerDown={e => {
        e.stopPropagation()
        select()
        if (!editing) return
        drag.current = { x: e.clientX, y: e.clientY, ox: o.x, oy: o.y, moved: false }
        ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      }}
      onPointerMove={e => {
        const d = drag.current
        if (!d) return
        const z = rf.getZoom()
        const dx = (e.clientX - d.x) / z, dy = (e.clientY - d.y) / z
        if (!d.moved) {
          if (Math.hypot(dx, dy) < 2) return
          d.moved = true
          beginChange()
        }
        const next = {
          x: axis === 'y' ? 0 : Math.round(clamp(d.ox + dx)),
          y: axis === 'x' ? 0 : Math.round(clamp(d.oy + dy)),
        }
        rf.updateEdgeData(edgeId, { [offsetKey]: next })
      }}
      onPointerUp={e => { drag.current = null; (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) }}
      onDoubleClick={e => {
        e.stopPropagation()
        if (!editing || !offset) return
        beginChange()
        rf.updateEdgeData(edgeId, { [offsetKey]: undefined })
      }}
    >{children}</div>
  )
}

function makeEdge(kind: PathKind) {
  return memo((p: EdgeProps<PortEdgeT>) => {
    const editing = useContext(EditModeContext)
    const srcNode = useInternalNode(p.source)
    const tgtNode = useInternalNode(p.target)
    const d = p.data || {}

    // Endpoints, slid along their node side by the user's shift (clamped to roughly the node size)
    const shiftLimit = (n: typeof srcNode, pos: Position) =>
      Math.max(0, ((alongX(pos) ? n?.measured?.width : n?.measured?.height) ?? 40) / 2 - 4)
    const sLim = shiftLimit(srcNode, p.sourcePosition), tLim = shiftLimit(tgtNode, p.targetPosition)
    const shifted = (x: number, y: number, pos: Position, s: XY | undefined, lim: number) => {
      if (!s) return { x, y }
      return alongX(pos)
        ? { x: x + Math.max(-lim, Math.min(lim, s.x)), y }
        : { x, y: y + Math.max(-lim, Math.min(lim, s.y)) }
    }
    const src = shifted(p.sourceX, p.sourceY, p.sourcePosition, d.sourceShift, sLim)
    const tgt = shifted(p.targetX, p.targetY, p.targetPosition, d.targetShift, tLim)
    const args = { sourceX: src.x, sourceY: src.y, targetX: tgt.x, targetY: tgt.y, sourcePosition: p.sourcePosition, targetPosition: p.targetPosition }

    // Path + the point the middle knob / label sits on
    let path: string, labelX: number, labelY: number
    const mid = { x: (src.x + tgt.x) / 2, y: (src.y + tgt.y) / 2 }
    const bend = d.bend
    if (kind === 'smoothstep' || kind === 'step') {
      ;[path, labelX, labelY] = getSmoothStepPath({
        ...args, borderRadius: kind === 'step' ? 0 : 8,
        ...(bend ? { centerX: mid.x + bend.x, centerY: mid.y + bend.y } : {}),
      })
    } else if (bend) {
      const bp = { x: mid.x + bend.x, y: mid.y + bend.y }
      if (kind === 'straight') {
        path = `M ${src.x},${src.y} L ${bp.x},${bp.y} L ${tgt.x},${tgt.y}`
      } else {
        // Quadratic curve that passes through the bend point at its midpoint
        const c = { x: 2 * bp.x - mid.x, y: 2 * bp.y - mid.y }
        path = `M ${src.x},${src.y} Q ${c.x},${c.y} ${tgt.x},${tgt.y}`
      }
      labelX = bp.x; labelY = bp.y
    } else {
      ;[path, labelX, labelY] = kind === 'straight' ? getStraightPath(args) : getBezierPath(args)
    }
    // Knob position for the bend handle: the path's middle. Offsets are applied by <Draggable>,
    // so pass the automatic (un-bent) point as its anchor.
    const bendAnchor = { x: labelX - (bend?.x ?? 0), y: labelY - (bend?.y ?? 0) }

    const color = d.color || '#1d4ed8'
    const size = d.labelSize || 11
    const ring = p.selected ? '0 0 0 2px rgba(59,130,246,.5)' : undefined
    const portStyle: React.CSSProperties = {
      fontSize: Math.max(8, size - 1.5), fontWeight: 600, lineHeight: 1.25, whiteSpace: 'nowrap',
      padding: '0 4px', borderRadius: 3, background: '#ffffff', color: '#111827',
      border: `1px solid ${color}`, boxShadow: ring, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
    }
    const label = typeof p.label === 'string' || typeof p.label === 'number' ? String(p.label) : ''
    const knobs = editing && p.selected
    // For straight lines the port anchor follows the first/last segment (toward the bend point if any)
    const bentMid = bend && kind !== 'smoothstep' && kind !== 'step' ? { x: labelX, y: labelY } : null
    return (
      <>
        <BaseEdge id={p.id} path={path} style={p.style} markerStart={p.markerStart} markerEnd={p.markerEnd} interactionWidth={p.interactionWidth}/>
        {(label || d.sourcePort || d.targetPort || knobs) && (
          <EdgeLabelRenderer>
            {label && (
              <Draggable edgeId={p.id} at={{ x: labelX, y: labelY }} offset={d.labelOffset} offsetKey="labelOffset"
                className={`nd-edge-label ${editing ? 'nd-edge-label-edit' : ''}`} style={{
                  fontSize: size, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'pre', padding: '1px 5px', borderRadius: 3,
                  color: d.labelColor || '#111827', background: d.labelBg || 'rgba(255,255,255,.9)', boxShadow: ring,
                }}>{label}</Draggable>
            )}
            {d.sourcePort && (
              <Draggable edgeId={p.id} offsetKey="sourcePortOffset" offset={d.sourcePortOffset} style={portStyle}
                className={`nd-edge-label ${editing ? 'nd-edge-label-edit' : ''}`}
                at={portPoint(kind, src.x, src.y, p.sourcePosition, bentMid?.x ?? tgt.x, bentMid?.y ?? tgt.y)}>{d.sourcePort}</Draggable>
            )}
            {d.targetPort && (
              <Draggable edgeId={p.id} offsetKey="targetPortOffset" offset={d.targetPortOffset} style={portStyle}
                className={`nd-edge-label ${editing ? 'nd-edge-label-edit' : ''}`}
                at={portPoint(kind, tgt.x, tgt.y, p.targetPosition, bentMid?.x ?? src.x, bentMid?.y ?? src.y)}>{d.targetPort}</Draggable>
            )}
            {knobs && (
              <>
                <Draggable edgeId={p.id} offsetKey="bend" offset={bend} at={bendAnchor} className="nd-knob nd-knob-bend"
                  title="ลากเพื่อดัดเส้น / ย้ายช่วงกลางของเส้น · ดับเบิลคลิกเพื่อคืนรูปเส้นเดิม"/>
                <Draggable edgeId={p.id} offsetKey="sourceShift" offset={d.sourceShift} at={outward(p.sourceX, p.sourceY, p.sourcePosition)}
                  axis={alongX(p.sourcePosition) ? 'x' : 'y'} limit={sLim} className="nd-knob nd-knob-end"
                  title="ลากเพื่อเลื่อนจุดออกของเส้นตามขอบอุปกรณ์ · ดับเบิลคลิกเพื่อคืนตำแหน่งกลาง"/>
                <Draggable edgeId={p.id} offsetKey="targetShift" offset={d.targetShift} at={outward(p.targetX, p.targetY, p.targetPosition)}
                  axis={alongX(p.targetPosition) ? 'x' : 'y'} limit={tLim} className="nd-knob nd-knob-end"
                  title="ลากเพื่อเลื่อนจุดเข้าของเส้นตามขอบอุปกรณ์ · ดับเบิลคลิกเพื่อคืนตำแหน่งกลาง"/>
              </>
            )}
          </EdgeLabelRenderer>
        )}
      </>
    )
  })
}

// Registered under React Flow's built-in type names so saved diagrams keep working
export const edgeTypes = {
  straight: makeEdge('straight'),
  default: makeEdge('bezier'),
  smoothstep: makeEdge('smoothstep'),
  step: makeEdge('step'),
}
